import { Component, OnInit, OnDestroy } from '@angular/core';
import { VdiApprovalService, PendingApproval } from '../../services/vdi-approval.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Subject, takeUntil } from 'rxjs';
import { formatDistance } from 'date-fns';

@Component({
  selector: 'app-vdi-approval-dashboard',
  templateUrl: './vdi-approval-dashboard.component.html',
  styleUrls: ['./vdi-approval-dashboard.component.scss']
})
export class VdiApprovalDashboardComponent implements OnInit, OnDestroy {
  pendingApprovals: PendingApproval[] = [];
  loading = false;
  canApprove = false;
  private destroy$ = new Subject<void>();
  private refreshInterval?: any;

  constructor(
    private vdiApprovalService: VdiApprovalService,
    private message: NzMessageService,
    private modal: NzModalService
  ) {}

  ngOnInit(): void {
    this.loadPendingApprovals();
    this.checkApprovalPermissions();
    this.startAutoRefresh();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopAutoRefresh();
  }

  loadPendingApprovals(): void {
    this.loading = true;
    this.vdiApprovalService.getPendingApprovals()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (approvals) => {
          this.pendingApprovals = approvals;
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          this.message.error('Failed to load pending approvals');
          console.error('Error loading pending approvals:', error);
        }
      });
  }

  checkApprovalPermissions(): void {
    this.vdiApprovalService.canUserApproveTickets()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (canApprove) => {
          this.canApprove = canApprove;
        },
        error: () => {
          this.canApprove = false;
        }
      });
  }

  approveTicket(approval: PendingApproval): void {
    this.showApprovalModal('approve', approval);
  }

  rejectTicket(approval: PendingApproval): void {
    this.showApprovalModal('reject', approval);
  }

  private showApprovalModal(action: 'approve' | 'reject', approval: PendingApproval): void {
    const isApprove = action === 'approve';

    this.modal.create({
      nzTitle: `${isApprove ? 'Approve' : 'Reject'} VDI Request`,
      nzContent: `
        <div>
          <p>Are you sure you want to ${isApprove ? 'approve' : 'reject'} this VDI request?</p>
          <div class="approval-details">
            <p><strong>Ticket:</strong> ${approval.ticketNumber}</p>
            <p><strong>Requester:</strong> ${approval.requesterEmail}</p>
            <p><strong>Service:</strong> ${approval.serviceDescription || 'VDI Service'}</p>
            <p><strong>GPU Required:</strong> ${approval.gpuRequirement ? 'Yes' : 'No'}</p>
            <p><strong>Justification:</strong></p>
            <div class="justification-text">${approval.justification}</div>
            ${approval.remarks ? `<p><strong>Additional Remarks:</strong></p><div class="remarks-text">${approval.remarks}</div>` : ''}
          </div>
          ${isApprove ? `
          <div class="vm-selection">
            <label>VM Size</label>
            <select class="modal-select" #vmSizeSelect>
              <option value="">Use default</option>
              <option value="Standard_D2s_v3">Standard_D2s_v3 (2 vCPU, 8 GB)</option>
              <option value="Standard_D4s_v3">Standard_D4s_v3 (4 vCPU, 16 GB)</option>
              <option value="Standard_D8s_v3">Standard_D8s_v3 (8 vCPU, 32 GB)</option>
              <option value="Standard_NV6">Standard_NV6 (GPU)</option>
            </select>
            <label>Region</label>
            <select class="modal-select" #regionSelect>
              <option value="" selected>Use default</option>
              <option value="eastasia">East Asia</option>
              <option value="southeastasia">Southeast Asia</option>
              <option value="japaneast">Japan East</option>
            </select>
          </div>` : ''}
          <textarea nz-input placeholder="Optional comments for ${isApprove ? 'approval' : 'rejection'}..." rows="4" #commentsTextarea></textarea>
        </div>
      `,
      nzOkText: isApprove ? 'Approve' : 'Reject',
      nzOkType: isApprove ? 'primary' : 'default',
      nzOkDanger: !isApprove,
      nzCancelText: 'Cancel',
      nzWidth: 700,
      nzOnOk: (componentInstance: any) => {
        const comments = componentInstance?.commentsTextarea?.value || '';
        const vmSize = componentInstance?.vmSizeSelect?.value || undefined;
        const region = componentInstance?.regionSelect?.value || undefined;
        return this.performApprovalAction(action, approval.ticketNumber, approval.id, comments, vmSize, region);
      }
    });
  }

  private performApprovalAction(action: 'approve' | 'reject', ticketNumber: string, ticketId: number, comments?: string, vmSize?: string, region?: string): Promise<void> {
    const source$ = action === 'approve'
      ? this.vdiApprovalService.approveTicket(ticketNumber, { comments, vmSize, region })
      : this.vdiApprovalService.rejectTicket(ticketId, comments);

    return new Promise((resolve, reject) => {
      source$.pipe(takeUntil(this.destroy$)).subscribe({
        next: (response) => {
          const actionText = action === 'approve' ? 'approved' : 'rejected';
          this.message.success(`VDI request ${actionText} successfully`);
          this.loadPendingApprovals(); // Refresh the list
          resolve();
        },
        error: (error) => {
          const actionText = action === 'approve' ? 'approval' : 'rejection';
          this.message.error(`Failed to ${actionText} VDI request: ${error.error?.message || 'Unknown error'}`);
          reject(error);
        }
      });
    });
  }

  refresh(): void {
    this.loadPendingApprovals();
    this.checkApprovalPermissions();
  }

  private startAutoRefresh(): void {
    this.refreshInterval = setInterval(() => {
      this.loadPendingApprovals();
    }, 60000); // Refresh every minute
  }

  private stopAutoRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = undefined;
    }
  }

  getRelativeTime(date: string): string {
    try {
      return formatDistance(new Date(date), new Date(), { addSuffix: true });
    } catch {
      return date;
    }
  }

  trackByApprovalId(_: number, approval: PendingApproval): number {
    return approval.id;
  }

  getGpuLabel(gpuRequired: boolean): string {
    return gpuRequired ? 'GPU Required' : 'Standard';
  }

  getGpuColor(gpuRequired: boolean): string {
    return gpuRequired ? 'orange' : 'green';
  }

  // Statistics
  get totalPending(): number {
    return this.pendingApprovals.length;
  }

  get gpuPending(): number {
    return this.pendingApprovals.filter(a => a.gpuRequirement).length;
  }

  get standardPending(): number {
    return this.pendingApprovals.filter(a => !a.gpuRequirement).length;
  }

  get averageWaitTime(): string {
    if (this.pendingApprovals.length === 0) return '0 minutes';

    const now = new Date();
    const totalWaitTime = this.pendingApprovals.reduce((total, approval) => {
      const createdDate = new Date(approval.createdDate);
      const waitTime = now.getTime() - createdDate.getTime();
      return total + waitTime;
    }, 0);

    const averageWaitTime = totalWaitTime / this.pendingApprovals.length;
    const hours = Math.floor(averageWaitTime / (1000 * 60 * 60));
    const minutes = Math.floor((averageWaitTime % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }
}